import React, { useState, useEffect, useRef } from "react";
import TestimonialForm from "./components/TestimonialForm";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, Outlet, useNavigate, useLocation } from "react-router-dom";

// ── Scroll to top on every route change ──────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataModeProvider } from "@/contexts/DataModeContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { DropdownProvider } from "@/contexts/DropdownContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AdminLogin from "@/components/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import RequireAuth from "@/components/guards/RequireAuth";
import RequireRole from "@/components/guards/RequireRole";
import UltraLayout from "@/pages/ultra/UltraLayout";
import UltraDashboard from "@/pages/ultra/UltraDashboard";
import UltraOrgs from "@/pages/ultra/UltraOrgs";
import UltraUsers from "@/pages/ultra/UltraUsers";
import UltraPlans from "@/pages/ultra/UltraPlans";
import UltraSubscriptions from "@/pages/ultra/UltraSubscriptions";
import UltraAudit from "@/pages/ultra/UltraAudit";
import UltraWebhooks from "@/pages/ultra/UltraWebhooks";
import UltraSettings from "@/pages/ultra/UltraSettings";
import UltraSuppliers from "@/pages/ultra/UltraSuppliers";
import UltraBriefs from "@/pages/ultra/UltraBriefs";
import CategoryLanding from "@/pages/CategoryLanding";
import UltraScheduler from "@/pages/ultra/UltraScheduler";
import UltraCMS from "@/pages/ultra/UltraCMS";
import UltraDropdowns from "@/pages/ultra/UltraDropdowns";
import UltraRules from "@/pages/ultra/UltraRules";
import UltraTestimonials from "@/pages/ultra/UltraTestimonials";
import UltraCredits from "@/pages/ultra/UltraCredits";
import UltraFeaturedListings from "@/pages/ultra/UltraFeaturedListings";
import UltraInquiries from "@/pages/ultra/UltraInquiries";
import UltraPlatformFeatures from "@/pages/ultra/UltraPlatformFeatures";
import UltraEmailManagement from "@/pages/ultra/UltraEmailManagement";
import { Toaster } from "@/components/ui/sonner";
import ExhibitorBriefWizard from "@/pages/exhibitor/ExhibitorBriefWizard";
import ExhibitorLayout from "@/pages/exhibitor/ExhibitorLayout";
import ExhibitorDashboard from "@/pages/exhibitor/ExhibitorDashboard";
import ExhibitorOnboarding from "@/pages/exhibitor/ExhibitorOnboarding";
import ExhibitorBriefs from "@/pages/exhibitor/ExhibitorBriefs";
import FindContractors from "@/pages/exhibitor/FindContractors";
import ExhibitorProposals from "@/pages/exhibitor/ExhibitorProposals";
import ExhibitorCompanyProfile from "@/pages/exhibitor/ExhibitorCompanyProfile";
import ExhibitorTeam from "@/pages/exhibitor/ExhibitorTeam";
import ExhibitorInvoices from "@/pages/exhibitor/ExhibitorInvoices";
import ExhibitorAnalytics from "@/pages/exhibitor/ExhibitorAnalytics";
import ContractorLayout from "@/pages/contractor/ContractorLayout";
import ContractorMessages from "@/pages/contractor/ContractorMessages";
import ContractorDashboard from "@/pages/contractor/ContractorDashboard";
import ContractorProjects from "@/pages/contractor/ContractorProjects";
import ContractorBriefBoard from "@/pages/contractor/ContractorBriefBoard";
import ContractorMyProposals from "@/pages/contractor/ContractorMyProposals";
import ContractorProfile from "@/pages/contractor/ContractorProfile";
import ContractorTeam from "@/pages/contractor/ContractorTeam";
import CreditsPage from "@/pages/contractor/CreditsPage";
import ContractorInvoices from "@/pages/contractor/ContractorInvoices";
import ContractorAnalytics from "@/pages/contractor/ContractorAnalytics";
import ContractorOnboarding from "@/pages/contractor/ContractorOnboarding";
import BillingPage from "@/pages/BillingPage";
import MessagingPage from "@/pages/MessagingPage";
import SavedSearchesPage from "@/pages/SavedSearchesPage";
import SupplierLayout from "@/pages/supplier/SupplierLayout";
import SupplierDashboard from "@/pages/supplier/SupplierDashboard";
import SupplierProfile from "@/pages/supplier/SupplierProfile";
import SupplierCatalogue from "@/pages/supplier/SupplierCatalogue";
import SupplierTeam from "@/pages/supplier/SupplierTeam";
import SupplierInvoices from "@/pages/supplier/SupplierInvoices";
import SupplierOnboarding from "@/pages/supplier/SupplierOnboarding";
import SupplierAnalytics from "@/pages/supplier/SupplierAnalytics";
import FindBriefs from "@/pages/supplier/FindBriefs";
import ExhibitorSignup from "@/pages/ExhibitorSignup";
// ExhibitorOnboarding removed — signup now auto-redirects to dashboard
// ExhibitorProfile (legacy - replaced by ExhibitorCompanyProfile)
// import ExhibitorProfile from "@/pages/ExhibitorProfile";
import ExhibitorLanding from "@/pages/ExhibitorLanding";
import ExhibitorBriefSuccess from "@/pages/ExhibitorBriefSuccess";
import ContractorSignup from "@/pages/ContractorSignup";
import ContractorLanding from "@/pages/ContractorLanding";
import SupplierSignup from "@/pages/SupplierSignup";
import SupplierLanding from "@/pages/SupplierLanding";
import GetFreeQuote from "@/pages/GetFreeQuote";
import PremiumQuoteBenefits from "@/pages/PremiumQuoteBenefits";
import PremiumQuoteForm from "@/pages/PremiumQuoteForm";
import PremiumQuoteHandler from "@/pages/PremiumQuoteHandler";
import CityDetail from "@/pages/CityDetail";
import FairDetail from "@/pages/FairDetail";
import Top10Contractors from "@/pages/Top10Contractors";
import LocationsPage from "@/pages/LocationsPage";
import ContractorPublicProfile from "@/pages/ContractorPublicProfile";
import TeamMemberPage from "@/pages/TeamMemberPage";
import ContractorDiscover from "@/pages/ContractorDiscover";
import ContractorCompare from "@/pages/ContractorCompare";
import PublicFindContractors from "@/pages/PublicFindContractors";
import PublicFindSuppliers from "@/pages/PublicFindSuppliers";
import ExhibitorLogin from "@/pages/ExhibitorLogin";
import ContractorLogin from "@/pages/ContractorLogin";
import SupplierLogin from "@/pages/SupplierLogin";
import ExhibitorPublicProfile from "@/pages/ExhibitorPublicProfile";
import ExhibitorProfileEditor from "@/pages/exhibitor/ExhibitorProfile";
import UltraExhibitors from "@/pages/ultra/UltraExhibitors";
import UltraFairs from "@/pages/ultra/UltraFairs";
import UltraOrganizers from "@/pages/ultra/UltraOrganizers";
import SupplierPublicProfile from "@/pages/SupplierPublicProfile";
import FreelancerSignup from "@/pages/FreelancerSignup";
import FreelancerLogin from "@/pages/FreelancerLogin";
import FreelancerLanding from "@/pages/FreelancerLanding";
import FreelancerPublicProfile from "@/pages/FreelancerPublicProfile";
import PublicFindFreelancers from "@/pages/PublicFindFreelancers";
import FreelancerLayout from "@/pages/freelancer/FreelancerLayout";
import FreelancerDashboard from "@/pages/freelancer/FreelancerDashboard";
import FreelancerProfilePage from "@/pages/freelancer/FreelancerProfilePage";
import FreelancerInvoices from "@/pages/freelancer/FreelancerInvoices";
import JobBoardPage from "@/pages/JobBoardPage";
import UltraFreelancers from "@/pages/ultra/UltraFreelancers";
import UltraJobPosts from "@/pages/ultra/UltraJobPosts";
import { PublicNavbar, PublicFooter } from "@/components/PublicLayout";
import { useAuth } from "@/contexts/AuthContext";

// Layout wrapper for public signup/landing pages
const SignupLayout = () => (
  <div className="min-h-screen flex flex-col">
    <PublicNavbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <PublicFooter />
  </div>
);

// Role-based redirect after login
const AppRedirect = () => {
  const { user } = useAuth();
  if (!user || !user.org_memberships || user.org_memberships.length === 0) {
    return <Navigate to="/app/ultra/dashboard" replace />;
  }
  const role = user.org_memberships[0]?.role;
  if (role === 'EXHIBITOR_ADMIN') return <Navigate to="/app/exhibitor/dashboard" replace />;
  if (role === 'EXHIBITOR_MEMBER') return <Navigate to="/app/exhibitor/dashboard" replace />;
  if (role === 'CONTRACTOR_ADMIN') return <Navigate to="/app/contractor/dashboard" replace />;
  if (role === 'CONTRACTOR_MEMBER') return <Navigate to="/app/contractor/dashboard" replace />;
  if (role === 'SUPPLIER_ADMIN') return <Navigate to="/app/supplier/dashboard" replace />;
  if (role === 'SUPPLIER_MEMBER') return <Navigate to="/app/supplier/dashboard" replace />;
  if (role === 'FREELANCER_ADMIN') return <Navigate to="/app/freelancer/dashboard" replace />;
  if (role === 'FREELANCER_MEMBER') return <Navigate to="/app/freelancer/dashboard" replace />;
  return <Navigate to="/app/ultra/dashboard" replace />;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TESTIMONIALS = [
  { id: "1", name: "Pharma Innovations GmbH", role: "Marketing Director", tag: "Exhibitor", tag_color: "blue",
    text: "We needed a qualified stand builder in Düsseldorf in just 3 weeks. ExpoStand matched us with 5 verified contractors within hours. Our stand was delivered on time and within budget.",
    avatar: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=120&q=80", verified_label: "Verified Exhibitor" },
  { id: "2", name: "ExpoMaker Istanbul", role: "CEO, Exhibition Stand Constructor", tag: "Contractor", tag_color: "orange",
    text: "Since joining ExpoStand as a verified contractor, we've connected with exhibitors from 12 different countries. The brief system sends us perfectly targeted project requests — no more cold outreach.",
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=120&q=80", verified_label: "Verified Contractor" },
  { id: "3", name: "DisplayTech Materials", role: "Business Development, Supplier", tag: "Supplier", tag_color: "teal",
    text: "ExpoStand's supplier directory transformed our B2B reach. Contractors from across Europe now discover us directly through the platform. Our order volume doubled in the first year.",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&q=80", verified_label: "Verified Supplier" },
];

const DEFAULT_STATS = [
  { value: "2,500+", label: "Verified Matches" },
  { value: "50+",    label: "Countries" },
  { value: "98%",    label: "Satisfaction Rate" },
  { value: "24h",    label: "First Response" },
];

const DEFAULT_CMS = {
  cities: [
    { name: "ISTANBUL", slug: "istanbul", suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=900&q=85" },
    { name: "DUBAI",    slug: "dubai",    suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=900&q=85" },
    { name: "LONDON",   slug: "london",   suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=900&q=85" },
    { name: "PARIS",    slug: "paris",    suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=900&q=85" },
    { name: "LAS VEGAS",slug: "las-vegas",suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=900&q=85" },
    { name: "BEIJING",  slug: "beijing",  suppliers: "15 suppliers", image: "https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?w=900&q=85" },
  ],
  made_easy: { label: "Trade Show Stand", heading: "MADE\nEASY\nAND\nQUICK", sub_label: "For Exhibitors", description: "We've developed an efficient process to get your load on the road as smooth as possible.", world_label: "ALL OVER THE WORLD" },
  gallery: {
    title: "Most popular\nprojects from our\npremium\ncontractors",
    subtitle: "click to spend\nless time",
    images: [
      { id: "1", url: "https://images.pexels.com/photos/35138560/pexels-photo-35138560.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Trade Show Hall" },
      { id: "2", url: "https://images.unsplash.com/photo-1746040669847-d680018f7ecd?w=900&q=85", alt: "Architectural Display" },
      { id: "3", url: "https://images.unsplash.com/photo-1771402382398-7210f67eb41b?w=900&q=85", alt: "Modern Exhibition Showroom" },
      { id: "4", url: "https://images.pexels.com/photos/36522033/pexels-photo-36522033.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Industrial Trade Fair" },
      { id: "5", url: "https://images.unsplash.com/photo-1772516912380-d39c64f5a85f?w=900&q=85", alt: "Technology Display" },
      { id: "6", url: "https://images.unsplash.com/photo-1761195689615-9469b65dac01?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Corporate Exhibition Booth" },
      { id: "7", url: "https://images.unsplash.com/photo-1597713259736-28c72bf90c08?crop=entropy&cs=srgb&fm=jpg&q=85", alt: "Modern Exhibition Stand" },
    ],
  },
  hero: { description: "Don't worry, you won't pay anything ! Get quotes from reliable manufacturers in a short time." },
  directory: { headline: "More than 500+\nSuppliers Worldwide", subtext: "Find your best Contractors & Suppliers from all over the world" },
  supplier_cards: [
    { id:"1", title:"Need More?",         bg_color:"#C9B5A8", text_color:"#102347", link:"/find-suppliers",                         variant:"decorative" },
    { id:"2", title:"Creative Designers", bg_color:"#0B1E4D", text_color:"#ffffff", link:"/find-suppliers?product=Creative+Design", variant:"standard"  },
    { id:"3", title:"Catering Companies", bg_color:"#C9B5A8", text_color:"#0B1E4D", link:"/find-suppliers?product=Catering",        variant:"standard"  },
    { id:"4", title:"Plan Your Trip",     bg_color:"#0A6D7C", text_color:"#ffffff", link:"/find-suppliers?product=Travel",          variant:"standard"  },
    { id:"5", title:"PREMIUM SUPPLIERS",  bg_color:"#0A6D7C", text_color:"#ffffff", link:"/find-suppliers",                         variant:"cta"       },
    { id:"6", title:"Logistics Companies",bg_color:"#C9B5A8", text_color:"#0B1E4D", link:"/find-suppliers?product=Logistics",       variant:"standard"  },
    { id:"7", title:"Printing Services",  bg_color:"#0B1E4D", text_color:"#ffffff", link:"/find-suppliers?product=Printing",        variant:"standard"  },
    { id:"8", title:"Premium Services",   bg_color:"#1a1a1a", text_color:"#ffffff", link:"/find-suppliers?product=Premium",         variant:"image",
      image:"https://customer-assets.emergentagent.com/job_trade-booth-saas/artifacts/2p7gjmnq_Screenshot%202025-10-09%20at%2005.51.46.png" },
  ],
  premium_suppliers_section: {
    badge_text: "Verified Excellence",
    title_line1: "PREMIUM", title_line2: "SUPPLIERS",
    description1: "We personally visit and inspect our qualified business partners in their workshops. These carefully selected suppliers represent the pinnacle of excellence in exhibition stand construction.",
    description2: "Every step of the process is monitored and controlled by our expert team to ensure exceptional quality and timely delivery.",
    features: ["Workshop Inspections","Quality Assurance","Verified Partners","Global Network"],
    cta_primary_text: "DISCOVER SUPPLIERS", cta_primary_link: "/find-suppliers",
    cta_secondary_text: "View Portfolio",    cta_secondary_link: "/find-suppliers",
    image: "https://customer-assets.emergentagent.com/job_trade-booth-saas/artifacts/2p7gjmnq_Screenshot%202025-10-09%20at%2005.51.46.png",
    image_title: "Premium Workshop", image_subtitle: "Precision Woodworking",
    stat1_value: "15+", stat1_label: "Years Experience",
    stat2_value: "500+", stat2_label: "Stands Built",
    process_title: "Craftsmanship Process",
    process_items: [
      {label:"Wood Selection",    value:"Premium Grade",   color:"green"},
      {label:"Precision Cutting", value:"CNC Technology",  color:"blue"},
      {label:"Assembly",          value:"Hand Crafted",    color:"purple"},
      {label:"Finishing",         value:"Quality Control", color:"orange"},
    ],
  },
};

const PrivatePage = () => (
  <div style={{
    minHeight: '100vh',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'arial,sans-serif',
    padding: '0 24px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 80, maxWidth: 860, width: '100%' }}>
      {/* Left: text */}
      <div style={{ flex: '0 0 auto' }}>
        {/* Google logo */}
        <div style={{ marginBottom: 28 }}>
          <svg width="92" height="30" viewBox="0 0 92 30" xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="26" style={{ fontFamily: 'arial,sans-serif', fontSize: 30, fontWeight: 400, fill: 'none' }}>
              <tspan style={{ fill: '#4285F4' }}>G</tspan>
              <tspan style={{ fill: '#EA4335' }}>o</tspan>
              <tspan style={{ fill: '#FBBC05' }}>o</tspan>
              <tspan style={{ fill: '#4285F4' }}>g</tspan>
              <tspan style={{ fill: '#34A853' }}>l</tspan>
              <tspan style={{ fill: '#EA4335' }}>e</tspan>
            </text>
          </svg>
        </div>
        <p style={{ margin: '0 0 16px 0', fontSize: 16, color: '#202124', lineHeight: 1.5 }}>
          <strong>403.</strong> <span style={{ color: '#202124' }}>That's an error.</span>
        </p>
        <p style={{ margin: 0, fontSize: 14, color: '#202124', lineHeight: 1.6 }}>
          We're sorry, but you do not have access to this page.<br />
          <span style={{ color: '#70757a' }}>That's all we know.</span>
        </p>
      </div>
      {/* Right: robot illustration */}
      <div style={{ flex: '0 0 auto' }}>
        <svg width="220" height="220" viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Body */}
          <rect x="68" y="90" width="84" height="76" rx="8" stroke="#4A90D9" strokeWidth="2.5" fill="none"/>
          {/* Head */}
          <rect x="76" y="52" width="68" height="52" rx="8" stroke="#4A90D9" strokeWidth="2.5" fill="none"/>
          {/* Antenna */}
          <line x1="110" y1="52" x2="110" y2="36" stroke="#4A90D9" strokeWidth="2.5"/>
          <circle cx="110" cy="32" r="5" stroke="#4A90D9" strokeWidth="2.5" fill="none"/>
          {/* Eyes */}
          <circle cx="96" cy="72" r="7" stroke="#4A90D9" strokeWidth="2.5" fill="none"/>
          <circle cx="124" cy="72" r="7" stroke="#4A90D9" strokeWidth="2.5" fill="none"/>
          <circle cx="96" cy="72" r="3" fill="#4A90D9"/>
          <circle cx="124" cy="72" r="3" fill="#4A90D9"/>
          {/* Mouth — wrench / broken */}
          <path d="M94 88 Q110 96 126 88" stroke="#4A90D9" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          {/* Left arm */}
          <line x1="68" y1="108" x2="44" y2="124" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="44" y1="124" x2="34" y2="140" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Wrench in left hand */}
          <circle cx="30" cy="148" r="7" stroke="#4A90D9" strokeWidth="2" fill="none"/>
          <line x1="34" y1="144" x2="48" y2="130" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Right arm — limp/broken */}
          <line x1="152" y1="108" x2="170" y2="128" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="170" y1="128" x2="162" y2="150" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Legs */}
          <line x1="94" y1="166" x2="88" y2="188" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="126" y1="166" x2="132" y2="188" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Feet */}
          <line x1="80" y1="188" x2="98" y2="188" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="124" y1="188" x2="142" y2="188" stroke="#4A90D9" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Scattered gears / bolts */}
          <circle cx="170" cy="160" r="8" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <circle cx="170" cy="160" r="4" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <line x1="170" y1="150" x2="170" y2="148" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="170" y1="170" x2="170" y2="172" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="160" y1="160" x2="158" y2="160" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="180" y1="160" x2="182" y2="160" stroke="#4A90D9" strokeWidth="2"/>
          <circle cx="52" cy="170" r="6" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <circle cx="52" cy="170" r="3" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <line x1="52" y1="162" x2="52" y2="160" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="52" y1="178" x2="52" y2="180" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="44" y1="170" x2="42" y2="170" stroke="#4A90D9" strokeWidth="2"/>
          <line x1="60" y1="170" x2="62" y2="170" stroke="#4A90D9" strokeWidth="2"/>
          {/* Small parts scattered */}
          <rect x="140" y="180" width="10" height="6" rx="1" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <rect x="60" y="194" width="8" height="5" rx="1" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <circle cx="186" cy="140" r="4" stroke="#4A90D9" strokeWidth="1.5" fill="none"/>
          <line x1="42" y1="146" x2="52" y2="152" stroke="#4A90D9" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  </div>
);

const HeroBtn = ({ to, label, hoverBg }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Link to={to}>
      <button
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: hovered
            ? `linear-gradient(135deg, ${hoverBg}cc 0%, ${hoverBg}88 60%, ${hoverBg}55 100%)`
            : 'rgba(255,255,255,0.22)',
          backdropFilter: hovered ? 'blur(18px) saturate(1.4)' : 'blur(8px)',
          WebkitBackdropFilter: hovered ? 'blur(18px) saturate(1.4)' : 'blur(8px)',
          boxShadow: hovered
            ? `0 8px 32px ${hoverBg}66, inset 0 1px 0 rgba(255,255,255,0.25)`
            : 'inset 0 1px 0 rgba(255,255,255,0.15)',
          border: hovered
            ? `1px solid ${hoverBg}99`
            : '1px solid rgba(255,255,255,0.18)',
          transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
          color: '#fff',
          borderRadius: 8,
          width: '100%',
          maxWidth: 320,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: 1,
          cursor: 'pointer',
          lineHeight: 1.2,
          textTransform: 'uppercase',
          transform: hovered ? 'translateY(-2px) scale(1.01)' : 'translateY(0) scale(1)',
        }}
      >
        {/* shine sweep on hover */}
        <span style={{
          position: 'absolute',
          inset: 0,
          background: hovered
            ? 'linear-gradient(105deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 70%)'
            : 'none',
          transition: 'opacity 0.3s',
          pointerEvents: 'none',
        }} />
        <span style={{ position: 'relative', zIndex: 1 }}>
          {label.split('\n').map((l, i) => (
            <React.Fragment key={i}>{l}{i === 0 && label.includes('\n') && <br />}</React.Fragment>
          ))}
        </span>
      </button>
    </Link>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const [cms, setCms] = useState(DEFAULT_CMS);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Hero search state
  const [searchType, setSearchType] = useState('contractor'); // 'contractor' | 'supplier'
  const [searchWhat, setSearchWhat] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchFair, setSearchFair] = useState('');

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDashboardRoute = (u) => {
    if (!u?.org_memberships?.length) return '/app/ultra/dashboard';
    const hasUltra = u.org_memberships.some(m => m.role === 'ULTRA_ADMIN');
    if (hasUltra) return '/app/ultra/dashboard';
    const role = u.org_memberships[0]?.role;
    if (role === 'EXHIBITOR_ADMIN' || role === 'EXHIBITOR_MEMBER') return '/app/exhibitor/dashboard';
    if (role === 'CONTRACTOR_ADMIN' || role === 'CONTRACTOR_MEMBER') return '/app/contractor/dashboard';
    if (role === 'SUPPLIER_ADMIN' || role === 'SUPPLIER_MEMBER') return '/app/supplier/dashboard';
    if (role === 'FREELANCER_ADMIN' || role === 'FREELANCER_MEMBER') return '/app/freelancer/dashboard';
    return '/app';
  };

  const getRoleLabel = (u) => {
    if (!u?.org_memberships?.length) return 'Ultra Admin';
    const hasUltra = u.org_memberships.some(m => m.role === 'ULTRA_ADMIN');
    if (hasUltra) return 'Ultra Admin';
    const role = u.org_memberships[0]?.role;
    if (role === 'EXHIBITOR_ADMIN' || role === 'EXHIBITOR_MEMBER') return 'Exhibitor';
    if (role === 'CONTRACTOR_ADMIN' || role === 'CONTRACTOR_MEMBER') return 'Contractor';
    if (role === 'SUPPLIER_ADMIN' || role === 'SUPPLIER_MEMBER') return 'Supplier';
    if (role === 'FREELANCER_ADMIN' || role === 'FREELANCER_MEMBER') return 'Freelancer';
    return 'User';
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/');
  };

  useEffect(() => {
    fetch(`${API_BASE}/public/homepage-content`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setCms({ ...DEFAULT_CMS, ...data }); })
      .catch(() => {/* use defaults */});
  }, []);

  const cities = cms.cities || DEFAULT_CMS.cities;
  const galleryImages = cms.gallery?.images || DEFAULT_CMS.gallery.images;
  const madeEasy = cms.made_easy || DEFAULT_CMS.made_easy;
  const galleryTitle = cms.gallery?.title || DEFAULT_CMS.gallery.title;
  const gallerySubtitle = cms.gallery?.subtitle || DEFAULT_CMS.gallery.subtitle;
  const supplierCards = cms.supplier_cards || DEFAULT_CMS.supplier_cards || [];
  const ps = cms.premium_suppliers_section || DEFAULT_CMS.premium_suppliers_section;

  const testimonials = cms.testimonials_section?.items || DEFAULT_TESTIMONIALS;
  const testimonialsSection = cms.testimonials_section || {};

  // Showcase (Vitrin) - dinamik galeri
  const [showcaseItems, setShowcaseItems] = useState([]);
  const resolveShowcaseImg = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${API_BASE}/contractor/profile/assets/${path}`;
  };
  useEffect(() => {
    fetch(`${API_BASE}/public/showcase?limit=7`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.items?.length > 0) setShowcaseItems(data.items); })
      .catch(() => {});
  }, []);

  // Canlı testimonials state (API'dan çekilir)
  const [liveTestimonials, setLiveTestimonials] = useState({ featured: [], approved: [], total_approved: 0, total_pages: 1, page: 1 });
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/testimonials?page=1&limit=9`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) { setLiveTestimonials(data); setTestimonialsLoaded(true); } })
      .catch(() => {});
  }, []);

  // Gösterilecek featured: canlı DB'den varsa onlar, yoksa CMS / DEFAULT
  const featuredToShow = liveTestimonials.featured?.length > 0
    ? liveTestimonials.featured
    : testimonials.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Background Image */}
      <section 
        className="min-h-screen bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(25, 47, 89, 0.75), rgba(25, 47, 89, 0.75)), url('https://boothshop.my.canva.site/master-file/images/3705d3d654b875fe97b9e5a8584a41a0.jpg')`
        }}
      >
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-50 px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
                alt="Logo" 
                className="w-10 h-8 md:w-12 md:h-10" 
              />
              <div className="text-white">
                <div className="text-lg md:text-xl font-semibold leading-tight">EXPO</div>
                <div className="text-lg md:text-xl font-semibold leading-tight">STAND</div>
              </div>
            </div>
            
            {/* Top Navigation - hidden on mobile */}
            <div className="hidden md:flex items-center space-x-6 text-white text-sm">
              <div className="flex items-center space-x-2">
                <img 
                  src="https://boothshop.my.canva.site/master-file/images/e0b2faa1efd8c7150c4183bccc279be5.svg" 
                  alt="Mail" 
                  className="w-4 h-4" 
                />
                <span>hello@expostand.com</span>
              </div>

              {/* ── Logged-in: user avatar dropdown ── */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(v => !v)}
                    className="flex items-center gap-2.5 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-xl hover:bg-white/30 transition-all border border-white/20"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-bold text-sm text-white uppercase">
                      {(user.name || user.email || '?').charAt(0)}
                    </div>
                    <div className="text-left max-w-[140px]">
                      <p className="text-white text-sm font-semibold leading-tight truncate">{user.name || user.email}</p>
                      <p className="text-white/60 text-xs truncate">{user.email}</p>
                    </div>
                    <svg className={`w-3.5 h-3.5 text-white/70 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white shadow-xl rounded-xl border border-gray-100 py-1.5 w-52 z-[200]" data-testid="user-menu-dropdown">
                      {/* User info header */}
                      <div className="px-3 py-2.5 border-b border-gray-100">
                        <p className="text-xs font-bold text-gray-900 truncate">{user.name || '—'}</p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        <span className="inline-block mt-1 text-xs font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">{getRoleLabel(user)}</span>
                      </div>

                      {/* Menu items */}
                      <div className="py-1">
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate(getDashboardRoute(user)); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          data-testid="user-menu-profile"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          {t('nav.profile')}
                        </button>
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate(getDashboardRoute(user)); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          data-testid="user-menu-security"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          {t('nav.security')}
                        </button>
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate(getDashboardRoute(user)); }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          data-testid="user-menu-settings"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                          {t('nav.settings')}
                        </button>
                      </div>

                      <div className="border-t border-gray-100 pt-1 pb-0.5">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors text-left"
                          data-testid="user-menu-logout"
                        >
                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Şehirler link */}
                  <Link to="/locations" className="hidden md:flex items-center gap-1 text-white/90 hover:text-white font-medium text-sm">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Şehirler
                  </Link>
                  {/* Sign In Dropdown */}
                  <div className="relative group">
                    <span className="hover:underline cursor-pointer flex items-center">
                      {t('nav.signIn')} <span className="ml-1">▾</span>
                    </span>
                    <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                      <Link to="/exhibitor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.exhibitor')}</Link>
                      <Link to="/contractor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.contractor')}</Link>
                      <Link to="/supplier/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.supplier')}</Link>
                      <Link to="/freelancer/login" className="block px-4 py-2 text-sm text-red-600 font-medium hover:bg-red-50">Freelancer</Link>
                      <div className="border-t border-gray-100 my-1" />
                      <Link to="/admin-login" className="block px-4 py-2 text-sm text-red-600 font-medium hover:bg-red-50">{t('nav.adminLogin')}</Link>
                    </div>
                  </div>
                  {/* Sign Up Dropdown */}
                  <div className="relative group">
                    <span className="hover:underline cursor-pointer flex items-center">
                      {t('nav.signUp')} <span className="ml-1">▾</span>
                    </span>
                    <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                      <Link to="exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.exhibitor')}</Link>
                      <Link to="contractor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.contractor')}</Link>
                      <Link to="supplier/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('nav.supplier')}</Link>
                      <Link to="/freelancer/landing" className="block px-4 py-2 text-sm text-red-600 font-medium hover:bg-red-50">Freelancer</Link>
                    </div>
                  </div>
                  <span className="hover:underline cursor-pointer">{t('nav.contact')}</span>
                </>
              )}
              {/* Language Switcher */}
              <LanguageSwitcher variant="light" />
            </div>

            {/* Mobile: show avatar or Sign In */}
            {user ? (
              <button
                onClick={() => navigate(getDashboardRoute(user))}
                className="md:hidden flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20"
              >
                <div className="w-7 h-7 rounded-full bg-white/30 flex items-center justify-center font-bold text-xs text-white uppercase">
                  {(user.name || user.email || '?').charAt(0)}
                </div>
                <span className="text-white text-sm font-semibold">Profil</span>
              </button>
            ) : (
              <Link to="/exhibitor/login" className="md:hidden px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-lg border border-white/30 hover:bg-white/30 transition-colors">
                {t('nav.signIn')}
              </Link>
            )}
          </div>
        </header>

        {/* Yellow asterisk */}
        <div className="absolute top-8 right-8 text-yellow-400 text-xl">
          ✱
        </div>

        {/* Main Hero Content */}
        <div className="flex items-start min-h-screen pt-24 md:pt-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Left Side Content */}
              <div className="md:col-span-7 text-white">
                {/* Main Headline */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-6 md:mb-8 leading-none tracking-tight">
                  {t('hero.headline1')}<br />
                  {t('hero.headline2')}<br />
                  {t('hero.headline3')}
                </h1>
                
                {/* Description */}
                <div className="mb-6 max-w-md">
                  <p className="text-base md:text-lg leading-tight mb-1">{t('hero.desc1')}</p>
                  <p className="text-base md:text-lg leading-tight mb-1">{t('hero.desc2')}</p>
                  <p className="text-base md:text-lg leading-tight mb-4">{t('hero.desc3')}</p>
                  <p className="text-base md:text-lg text-yellow-300">{t('hero.tagline')}</p>
                </div>
                
                {/* Search Form */}
                <div className="space-y-3 mb-8 md:mb-12 mt-6 md:mt-12">
                  {/* Contractor / Supplier Toggle */}
                  <div className="flex items-center gap-2 max-w-lg">
                    <button
                      onClick={() => setSearchType('contractor')}
                      data-testid="search-toggle-contractor"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${searchType === 'contractor' ? 'bg-white text-gray-900 shadow-md' : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'}`}
                    >
                      🔨 Find Contractor
                    </button>
                    <button
                      onClick={() => setSearchType('supplier')}
                      data-testid="search-toggle-supplier"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${searchType === 'supplier' ? 'bg-white text-gray-900 shadow-md' : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'}`}
                    >
                      📦 Find Supplier
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                    <div>
                      <Input
                        value={searchWhat}
                        onChange={e => setSearchWhat(e.target.value)}
                        placeholder={searchType === 'contractor' ? 'Service (e.g. Stand Design)' : 'Product (e.g. Digital Printing)'}
                        className="bg-white text-gray-800 h-12 text-sm border-0 rounded-md placeholder:text-gray-600"
                        data-testid="hero-search-what"
                      />
                    </div>
                    <div>
                      <Input
                        value={searchCity}
                        onChange={e => setSearchCity(e.target.value)}
                        placeholder="City (e.g. Frankfurt)"
                        className="bg-white text-gray-800 h-12 text-sm border-0 rounded-md placeholder:text-gray-600"
                        data-testid="hero-search-city"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 max-w-lg">
                    <Input
                      value={searchFair}
                      onChange={e => setSearchFair(e.target.value)}
                      placeholder={searchType === 'contractor' ? 'Fair (e.g. Hannover Messe)' : 'Country (e.g. Germany)'}
                      className="bg-white text-gray-800 h-12 text-sm border-0 rounded-md flex-1 placeholder:text-gray-600"
                      data-testid="hero-search-fair"
                    />
                    <Button
                      data-testid="hero-search-btn"
                      onClick={() => {
                        const params = new URLSearchParams();
                        if (searchWhat) params.set(searchType === 'contractor' ? 'service' : 'product', searchWhat);
                        if (searchCity) params.set('city', searchCity);
                        if (searchFair) params.set(searchType === 'contractor' ? 'fair' : 'country', searchFair);
                        const path = searchType === 'contractor' ? '/find-contractors' : '/find-suppliers';
                        navigate(`${path}?${params.toString()}`);
                      }}
                      className="bg-white hover:bg-gray-100 text-gray-900 px-4 md:px-6 py-3 text-sm font-bold h-12 rounded-md shrink-0"
                    >
                      {t('hero.next')}
                    </Button>
                  </div>
                </div>
                
                {/* Bottom Message */}
                <div className="space-y-1 mt-6 md:mt-8">
                  <p className="text-base md:text-lg font-normal">{t('hero.noPayment')}</p>
                  <p className="text-sm md:text-base leading-tight max-w-md">{t('hero.subDesc')}</p>
                </div>
              </div>

              <div className="md:col-span-5 flex flex-col space-y-4 md:space-y-5 md:pt-16">
                {[
                  { to: "/exhibitor/landing",   label: t('hero.iAmExhibitor'),   hoverBg: '#2563EB' },
                  { to: "/contractor/landing",  label: t('hero.iAmContractor'),  hoverBg: '#16A34A' },
                  { to: "/supplier/landing",    label: t('hero.iAmSupplier'),    hoverBg: '#7C3AED' },
                  { to: "/freelancer/landing",  label: 'I AM A\nFREELANCER',    hoverBg: '#DC2626' },
                ].map(({ to, label, hoverBg }) => (
                  <HeroBtn key={to} to={to} label={label} hoverBg={hoverBg} />
                ))}
              </div>
            </div>
            
            {/* Scroll indicators */}
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2">
              <div className="w-1 h-1 bg-white opacity-50 rotate-45 transform"></div>
              <div className="w-1 h-1 bg-white opacity-50 rotate-45 transform"></div>
            </div>
            
            <div className="absolute bottom-20 right-1/2 transform translate-x-32 flex flex-col items-center space-y-2">
              <div className="w-1 h-1 bg-white opacity-50 rotate-45 transform"></div>
              <div className="w-1 h-1 bg-white opacity-50 rotate-45 transform"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Reliable Contractor Directory Section */}
      <section className="w-full" style={{backgroundColor: '#E9EBEF'}}>
        <div className="mx-auto max-w-[1140px] px-4 md:px-6 lg:px-8 py-12 md:py-14 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-10 relative">
            {/* Decorative star (küçültüldü) */}
            <div className="hidden lg:block absolute right-0 top-1/3 translate-x-4">
              <span className="inline-block text-[48px] leading-none select-none opacity-60" style={{color: '#FFD54A'}}>✱</span>
            </div>

            {/* Left: text */}
            <div className="lg:col-span-8 xl:col-span-7">
              <p className="uppercase text-xs md:text-[13px] font-semibold tracking-wider text-[#2C3A5B]/70">
                {t('directory.label')}
              </p>

              <h1 className="mt-2 md:mt-3 text-[#1B2A57] font-extrabold leading-[1.05] text-4xl md:text-5xl lg:text-[56px] xl:text-[64px]">
                {t('directory.heading1')}<br className="hidden md:block" />
                {t('directory.heading2')}
              </h1>

              <p className="mt-4 max-w-[600px] text-[#3C4A6B]/85 text-base md:text-[17px] lg:text-[18px] leading-relaxed">
                {t('directory.subtext')}
              </p>

              <div className="mt-6">
                <a href="/get-quote" className="inline-flex items-center justify-center rounded-full bg-[#163A7D] px-6 md:px-7 py-3.5 text-white font-extrabold tracking-wide shadow-sm hover:shadow-md hover:brightness-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#163A7D]">
                  {t('directory.getFreeQuote')}
                </a>
              </div>
            </div>

            {/* Right: 3 stacked CTAs (daraltılmış) */}
            <div className="lg:col-span-4 xl:col-span-5 flex flex-col justify-center gap-4 md:gap-5 mt-8 lg:mt-0">
              <div className="flex justify-end">
                <a href="/get-quote" className="w-[280px] rounded-xl bg-[#1B3A8F] text-white font-extrabold uppercase text-sm md:text-base py-4 text-center tracking-wide hover:brightness-95 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3A8F]">
                  {t('directory.getFreeCta')}
                </a>
              </div>

              <div className="flex justify-end">
                <a href="/premium-quote" className="w-[280px] rounded-xl bg-[#1B3A8F] text-white font-extrabold uppercase text-sm md:text-base py-4 text-center tracking-wide hover:brightness-95 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3A8F]">
                  {t('directory.getPremiumCta')}
                </a>
              </div>

              <div className="flex justify-end">
                <a href="/top-10-contractors" className="w-[280px] rounded-xl bg-[#059669] text-white font-extrabold uppercase text-sm md:text-base py-4 text-center tracking-wide hover:brightness-95 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#059669]">
                  {t('directory.top10')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Made Easy and Quick + Cities Grid */}
      <section className="w-full bg-[#EBF4FF]">
        <div className="mx-auto max-w-[1140px] px-4 md:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">

            {/* LEFT: Copy block */}
            <div className="lg:col-span-4">
              <p className="uppercase text-xs md:text-sm font-semibold tracking-wider text-[#2C3A5B]/60">
                {madeEasy.label}
              </p>

              <h2 className="mt-2 text-[#142549] font-extrabold leading-[0.95]
                             text-[44px] md:text-[56px] lg:text-[64px]">
                {madeEasy.heading.split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < madeEasy.heading.split('\n').length - 1 && <br/>}</React.Fragment>)}
              </h2>

              <p className="mt-4 uppercase text-[11px] tracking-wide text-[#2C3A5B]/70">
                {madeEasy.sub_label}
              </p>

              <p className="mt-3 text-[15px] md:text-[16px] leading-relaxed text-[#3C4A6B]/90 max-w-[420px]">
                {madeEasy.description}
              </p>

              <p className="mt-6 text-sm tracking-wide text-[#142549]/80">
                {madeEasy.world_label}
              </p>

              {/* Tüm Şehirler butonu - sol alt */}
              <div className="mt-5">
                <Link
                  to="/locations"
                  data-testid="all-cities-btn"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Tüm Şehirler
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>

            {/* RIGHT: Cities 3x2 grid */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {cities.slice(0, 6).map((city, index) => (
                  <Link key={index} to={`/city/${city.slug}`} className="group">
                    <div className="aspect-[16/10] w-full overflow-hidden rounded-xl border border-slate-200">
                      <img src={city.image}
                           alt={city.name}
                           className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    </div>
                    <h3 className="mt-3 text-2xl font-extrabold tracking-wide text-[#142549]">{city.name}</h3>
                    <p className="text-[#3C4A6B]">{city.suppliers}</p>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PREMIUM SUPPLIERS - Dynamic Card Grid */}
      <section className="w-full bg-[#F5F5F5]">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {supplierCards.map((card, idx) => {
              const isSecondRow = idx >= 4;
              const heightCls = isSecondRow
                ? "h-[240px] md:h-[280px]"
                : "h-[200px] md:h-[240px]";

              if (card.variant === 'image') {
                return (
                  <Link key={card.id} to={card.link || '/find-suppliers'}
                    className={`group relative rounded-[20px] overflow-hidden ${heightCls} shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                    <img src={card.image} alt={card.title}
                      className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-4 right-4">
                      <div className="w-8 h-8 bg-[#0B1E4D] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <span className="text-white font-bold text-lg">+</span>
                      </div>
                    </div>
                    <div className="absolute left-6 bottom-6 text-white">
                      <div className="text-xl font-bold leading-tight drop-shadow-lg">
                        {card.title.split(' ').slice(0,1).join('')}<br/>{card.title.split(' ').slice(1).join(' ')}
                      </div>
                    </div>
                  </Link>
                );
              }

              if (card.variant === 'cta') {
                return (
                  <Link key={card.id} to={card.link || '/find-suppliers'}
                    className={`group relative rounded-[20px] ${heightCls} flex flex-col justify-between p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                    style={{ backgroundColor: card.bg_color, color: card.text_color }}>
                    <div className="flex-1 flex items-center">
                      <div className="text-2xl md:text-3xl font-bold leading-tight tracking-wide">
                        {card.title.split(' ').map((w,i) => <span key={i}>{w}<br/></span>)}
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="w-12 h-12 border-2 border-[#24B5D5] rounded-full flex items-center justify-center group-hover:border-white group-hover:scale-110 transition-all duration-300">
                        <svg viewBox="0 0 24 24" className="h-6 w-6"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
                      </div>
                    </div>
                  </Link>
                );
              }

              if (card.variant === 'decorative') {
                return (
                  <Link key={card.id} to={card.link || '/find-suppliers'}
                    className={`group relative rounded-[20px] ${heightCls} flex flex-col justify-between p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                    style={{ backgroundColor: card.bg_color, color: card.text_color }}>
                    <div className="absolute -top-4 left-4">
                      <div className="w-10 h-10 bg-[#0B1E4D] rounded-full flex items-center justify-center ring-4 ring-white group-hover:scale-110 transition-transform duration-300">
                        <span className="text-white text-lg">◇</span>
                      </div>
                    </div>
                    <div className="flex-1" />
                    <div className="text-xl font-bold leading-tight">
                      {card.title.split(' ').map((w,i) => <span key={i}>{w}<br/></span>)}
                    </div>
                  </Link>
                );
              }

              // standard
              const words = card.title.split(' ');
              const line1 = words.slice(0, Math.ceil(words.length / 2)).join(' ');
              const line2 = words.slice(Math.ceil(words.length / 2)).join(' ');
              return (
                <Link key={card.id} to={card.link || '/find-suppliers'}
                  className={`group relative rounded-[20px] ${heightCls} flex flex-col justify-between p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                  style={{ backgroundColor: card.bg_color, color: card.text_color }}>
                  <div className="absolute top-4 right-4">
                    <div className="w-8 h-8 bg-[#24B5D5] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="font-bold text-lg" style={{ color: card.bg_color === '#0B1E4D' ? '#0B1E4D' : 'white' }}>+</span>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <div className="text-xl font-bold leading-tight">
                    {line1}<br/>{line2}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREMIUM SUPPLIERS - Dynamic Split Design */}
      <section className="w-full bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 200 200" preserveAspectRatio="none">
              <defs><pattern id="woodgrain" width="40" height="8" patternUnits="userSpaceOnUse">
                <path d="M0,4 Q10,2 20,4 T40,4" stroke="white" strokeWidth="0.5" fill="none" opacity="0.3"/>
              </pattern></defs>
              <rect width="200" height="200" fill="url(#woodgrain)" />
            </svg>
          </div>
          <div className="absolute top-20 left-10 w-32 h-32 bg-amber-400/5 rounded-full blur-xl animate-pulse"/>
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-orange-400/5 rounded-full blur-xl animate-pulse" style={{animationDelay:'3s'}}/>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left: Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-300 text-sm font-semibold">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                {ps.badge_text}
              </div>

              <div className="space-y-4">
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.9] tracking-tight">
                  {ps.title_line1}
                  <br/>
                  <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    {ps.title_line2}
                  </span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-teal-400 rounded-full"/>
              </div>

              <div className="space-y-4">
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">{ps.description1}</p>
                <p className="text-lg text-gray-400 leading-relaxed">{ps.description2}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(ps.features || []).map((feature, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-teal-400 rounded-full flex-shrink-0"/>
                    <span className="text-gray-300 font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to={ps.cta_primary_link || '/find-suppliers'}
                  className="group relative bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 text-center">
                  <span className="relative z-10 flex items-center justify-center">
                    {ps.cta_primary_text}
                    <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </span>
                </Link>
                <Link to={ps.cta_secondary_link || '/find-suppliers'}
                  className="group border-2 border-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-white/30 transition-all duration-300 backdrop-blur-sm text-center">
                  <span className="flex items-center justify-center">
                    {ps.cta_secondary_text}
                    <svg className="ml-2 w-5 h-5 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </span>
                </Link>
              </div>
            </div>

            {/* Right: Visual Card */}
            <div className="relative">
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-all duration-500 group">
                <div className="relative mb-6 rounded-2xl overflow-hidden">
                  <img src={ps.image} alt={ps.image_title} className="w-full h-48 object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"/>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="text-sm font-semibold">{ps.image_title}</div>
                    <div className="text-xs text-gray-300">{ps.image_subtitle}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-amber-500/10 border border-amber-400/20 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-amber-400">{ps.stat1_value}</div>
                    <div className="text-xs text-amber-300">{ps.stat1_label}</div>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-400/20 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-orange-400">{ps.stat2_value}</div>
                    <div className="text-xs text-orange-300">{ps.stat2_label}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-semibold text-lg flex items-center">
                    <svg className="w-5 h-5 mr-2 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                    {ps.process_title}
                  </h4>
                  <div className="space-y-3">
                    {(ps.process_items || []).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300 flex items-center">
                          <div className={`w-2 h-2 bg-${item.color}-400 rounded-full mr-2`}/>
                          {item.label}
                        </span>
                        <span className={`text-${item.color}-400 font-semibold`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="grid grid-cols-4 gap-2">
                    {['🔨','🪚','📐','🔧'].map((tool,i) => (
                      <div key={i} className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-2xl hover:bg-white/10 transition-colors duration-300 cursor-pointer">{tool}</div>
                    ))}
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full shadow-lg group-hover:scale-125 transition-transform duration-300"/>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-amber-600/10 to-orange-600/10 rounded-3xl blur-3xl -z-10"/>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR PROJECTS / GALLERY MOSAIC */}
      <section className="w-full bg-white">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">

            {/* LEFT COLUMN */}
            <div className="lg:col-span-3 flex flex-col gap-6">
              {/* Text card */}
              <div className="rounded-[20px] bg-[#0B1E4D] text-white p-6 md:p-7 lg:p-8 shadow-sm">
                <p className="text-[22px] leading-snug font-medium">
                  {galleryTitle.split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < galleryTitle.split('\n').length - 1 && <br/>}</React.Fragment>)}
                </p>
                <p className="mt-6 text-[18px] leading-snug text-white/90">
                  {gallerySubtitle.split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < gallerySubtitle.split('\n').length - 1 && <br/>}</React.Fragment>)}
                </p>
                {showcaseItems.length > 0 && (
                  <p className="mt-3 text-xs text-[#1CC2E3]/80 font-medium">{showcaseItems.length} öne çıkan proje</p>
                )}
                <a href="#"
                   className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-full
                              border-2 border-[#1CC2E3] text-[#1CC2E3] hover:brightness-110 transition">
                  <span className="text-xl">➜</span>
                </a>
              </div>

              {/* Image card: First showcase or CMS fallback */}
              {(() => {
                const item = showcaseItems[0];
                const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[0]?.url || "https://images.pexels.com/photos/35138560/pexels-photo-35138560.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940");
                const label = item ? item.project_title : "PREMIUM Contractors";
                const sub = item ? item.org_name : null;
                const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                return (
                  <a href={href} className="relative overflow-hidden rounded-[20px] shadow-sm hover:shadow-md transition group">
                    <img src={img} alt={label} className="w-full h-[260px] md:h-[300px] object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute left-6 bottom-6 text-white">
                      <p className="text-[20px] font-semibold leading-tight drop-shadow">{label}</p>
                      {sub && <p className="text-sm text-white/80 mt-0.5">{sub}</p>}
                    </div>
                    {item && <span className="absolute top-3 left-3 bg-[#1CC2E3] text-white text-xs font-bold px-2 py-0.5 rounded-full">VİTRİN</span>}
                    <span className="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] font-bold">+</span>
                  </a>
                );
              })()}
            </div>

            {/* RIGHT COLUMN (MOSAIC) */}
            <div className="lg:col-span-9">
              <div className="grid grid-cols-3 gap-4 md:gap-5">

                {/* Top Row: Large Hero Image (spans 2 columns) */}
                {(() => {
                  const item = showcaseItems[1];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[1]?.url || galleryImages[0]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative col-span-2 overflow-hidden rounded-[16px] shadow-sm hover:shadow-md transition group">
                      <img src={img} alt={item?.project_title || "Exhibition Hall"} className="w-full h-[280px] md:h-[320px] object-cover group-hover:scale-105 transition-transform duration-500" />
                      {item && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute left-4 bottom-4 text-white">
                            <p className="text-sm font-semibold">{item.project_title}</p>
                            <p className="text-xs text-white/70">{item.org_name}</p>
                          </div>
                          <span className="absolute top-3 left-3 bg-[#1CC2E3] text-white text-xs font-bold px-2 py-0.5 rounded-full">VİTRİN</span>
                        </>
                      )}
                      <span className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] text-xl font-bold">+</span>
                    </a>
                  );
                })()}

                {/* Top Right: Tall Image (spans 2 rows) */}
                {(() => {
                  const item = showcaseItems[2];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[2]?.url || galleryImages[1]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative row-span-2 overflow-hidden rounded-[16px] shadow-sm hover:shadow-md transition group">
                      <img src={img} alt={item?.project_title || "Large Exhibition Space"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {item && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute left-3 bottom-4 text-white">
                            <p className="text-sm font-semibold leading-tight">{item.project_title}</p>
                            <p className="text-xs text-white/70">{item.org_name}</p>
                          </div>
                          <span className="absolute top-2 left-2 bg-[#1CC2E3] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">VİTRİN</span>
                        </>
                      )}
                      <span className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] font-bold">+</span>
                    </a>
                  );
                })()}

                {/* Middle Left */}
                {(() => {
                  const item = showcaseItems[3];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[3]?.url || galleryImages[2]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative overflow-hidden rounded-[12px] shadow-sm hover:shadow-md transition">
                      <img src={img} alt={item?.project_title || "Exhibition Booth"} className="w-full h-[120px] md:h-[140px] object-cover" />
                      {item && <span className="absolute top-1.5 left-1.5 bg-[#1CC2E3] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">VİTRİN</span>}
                      <span className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] text-sm font-bold">+</span>
                    </a>
                  );
                })()}

                {/* Middle Center */}
                {(() => {
                  const item = showcaseItems[4];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[4]?.url || galleryImages[3]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative overflow-hidden rounded-[12px] shadow-sm hover:shadow-md transition">
                      <img src={img} alt={item?.project_title || "Trade Show Stand"} className="w-full h-[120px] md:h-[140px] object-cover" />
                      {item && <span className="absolute top-1.5 left-1.5 bg-[#1CC2E3] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">VİTRİN</span>}
                      <span className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] text-sm font-bold">+</span>
                    </a>
                  );
                })()}

                {/* Bottom Left */}
                {(() => {
                  const item = showcaseItems[5];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[5]?.url || galleryImages[4]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative overflow-hidden rounded-[16px] shadow-sm hover:shadow-md transition">
                      <img src={img} alt={item?.project_title || "Exhibition Stand Display"} className="w-full h-[120px] md:h-[140px] object-cover" />
                      {item && <span className="absolute top-1.5 left-1.5 bg-[#1CC2E3] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">VİTRİN</span>}
                      <span className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] font-bold">+</span>
                    </a>
                  );
                })()}

                {/* Bottom Center */}
                {(() => {
                  const item = showcaseItems[6];
                  const img = item?.project_image ? resolveShowcaseImg(item.project_image) : (galleryImages[6]?.url || galleryImages[5]?.url);
                  const href = item?.org_slug ? `/contractor/${item.org_slug}` : "#";
                  return (
                    <a href={href} className="relative overflow-hidden rounded-[16px] shadow-sm hover:shadow-md transition">
                      <img src={img} alt={item?.project_title || "Exhibition Setup"} className="w-full h-[120px] md:h-[140px] object-cover" />
                      {item && <span className="absolute top-1.5 left-1.5 bg-[#1CC2E3] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">VİTRİN</span>}
                      <span className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0B1E4D] font-bold">+</span>
                    </a>
                  );
                })()}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* HOW IT WORKS - Modern Timeline Design */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full text-blue-800 text-sm font-semibold mb-6">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
              </svg>
              Simple Process
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              How It <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Transform your exhibition vision into reality with our streamlined three-step process
            </p>
          </div>

          {/* Timeline Process */}
          <div className="relative">
            
            {/* Central Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-200 via-purple-200 to-teal-200"></div>
            
            {/* Step 1 - Left */}
            <div className="relative mb-20 lg:mb-32">
              <div className="lg:flex lg:items-center">
                <div className="lg:w-1/2 lg:pr-12">
                  <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl 
                                  transition-all duration-500 hover:-translate-y-2 group">
                    
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl 
                                     flex items-center justify-center text-white font-bold text-xl mr-4
                                     group-hover:scale-110 transition-transform duration-300">
                        01
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                          Submit Your Project
                        </h3>
                        <p className="text-blue-600 font-semibold">Start Your Journey</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      Share your exhibition requirements including booth dimensions, design preferences, 
                      budget range, and event location. Our AI-powered matching system analyzes your needs 
                      and identifies the most suitable contractors from our verified global network.
                    </p>

                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      Free consultation included
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Average time:</span>
                        <span className="font-semibold text-blue-600">5 minutes</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                  <div className="w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>
                
                <div className="lg:w-1/2 lg:pl-12">
                  <div className="mt-8 lg:mt-0">
                    <img src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=800&auto=format&fit=crop" 
                         alt="Exhibition Planning" 
                         className="w-full h-64 lg:h-80 object-cover rounded-2xl shadow-lg" />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Right */}
            <div className="relative mb-20 lg:mb-32">
              <div className="lg:flex lg:items-center lg:flex-row-reverse">
                <div className="lg:w-1/2 lg:pl-12">
                  <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl 
                                  transition-all duration-500 hover:-translate-y-2 group">
                    
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl 
                                     flex items-center justify-center text-white font-bold text-xl mr-4
                                     group-hover:scale-110 transition-transform duration-300">
                        02
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors duration-300">
                          Review Proposals
                        </h3>
                        <p className="text-purple-600 font-semibold">Compare & Choose</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      Receive competitive proposals from premium contractors within 24 hours. 
                      Compare detailed portfolios, transparent pricing, timeline estimates, and 
                      past project galleries to make an informed decision.
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Verified contractors
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Transparent pricing
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Response time:</span>
                        <span className="font-semibold text-purple-600">24 hours</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                  <div className="w-6 h-6 bg-purple-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>
                
                <div className="lg:w-1/2 lg:pr-12">
                  <div className="mt-8 lg:mt-0">
                    <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=800&auto=format&fit=crop" 
                         alt="Proposal Review" 
                         className="w-full h-64 lg:h-80 object-cover rounded-2xl shadow-lg" />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Left */}
            <div className="relative">
              <div className="lg:flex lg:items-center">
                <div className="lg:w-1/2 lg:pr-12">
                  <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl 
                                  transition-all duration-500 hover:-translate-y-2 group">
                    
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl 
                                     flex items-center justify-center text-white font-bold text-xl mr-4
                                     group-hover:scale-110 transition-transform duration-300">
                        03
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                          Launch Project
                        </h3>
                        <p className="text-teal-600 font-semibold">Build & Deliver</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      Select your preferred contractor and begin the build process. Track project progress 
                      in real-time, communicate directly with your team, and ensure timely delivery 
                      for your exhibition date.
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Real-time tracking
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Quality guarantee
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Success rate:</span>
                        <span className="font-semibold text-teal-600">98%</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 -translate-y-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>
                
                <div className="lg:w-1/2 lg:pl-12">
                  <div className="mt-8 lg:mt-0">
                    <img src="https://customer-assets.emergentagent.com/job_trade-booth-saas/artifacts/6l809rai_Screenshot%202025-10-09%20at%2006.12.50.png" 
                         alt="Exhibition Stand - Project Completion" 
                         className="w-full h-64 lg:h-80 object-cover rounded-2xl shadow-lg" />
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-3xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-xl mb-8 opacity-90">Join thousands of satisfied exhibitors worldwide</p>
              <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg 
                               hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                Start Your Project Now
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {testimonialsSection.heading || "What Our Members Say"}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {testimonialsSection.subheading || "Trusted by exhibitors, contractors and suppliers worldwide"}
            </p>
          </div>

          {/* 3 Featured Kart */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredToShow.map((t, index) => {
              const tagColors = {
                blue:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200' },
                orange: { bg: 'bg-orange-50',  text: 'text-orange-700', border: 'border-orange-200' },
                teal:   { bg: 'bg-teal-50',    text: 'text-teal-700',   border: 'border-teal-200' },
                gray:   { bg: 'bg-gray-50',    text: 'text-gray-700',   border: 'border-gray-200' },
              };
              const tc = tagColors[t.tag_color] || tagColors.gray;
              return (
                <div key={t.id || index} className="bg-gray-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {t.avatar ? <img src={t.avatar} alt={t.name || t.user_name} className="w-14 h-14 rounded-full object-cover" />
                        : <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-xl font-bold">{(t.name || t.user_name || '?')[0]}</div>}
                      <div>
                        <h4 className="text-base font-bold text-gray-900">{t.name || t.user_name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{t.role || t.user_role}</p>
                      </div>
                    </div>
                    {t.tag && <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${tc.bg} ${tc.text} ${tc.border} whitespace-nowrap`}>{t.tag}</span>}
                  </div>
                  <div className="flex space-x-1 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} className={`w-4 h-4 ${s <= (t.rating || 5) ? 'text-yellow-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="text-gray-600 text-sm leading-relaxed italic flex-1">"{t.text}"</blockquote>
                  <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      {t.verified_label || "Verified Member"}
                    </span>
                    <span>ExpoStand Platform</span>
                  </div>
                </div>
              );
            })}
          </div>


          {/* Alt Butonlar — sadece Yorum Yaz */}
          <div className="flex items-center justify-center gap-4 mt-10 flex-wrap">
            <button
              onClick={() => setShowTestimonialForm(true)}
              className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 rounded-full shadow transition-opacity flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Yorum Yaz
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-gray-200">
            {(testimonialsSection.stats || DEFAULT_STATS).map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">{s.value}</div>
                <div className="text-gray-600 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Form Modal */}
      {showTestimonialForm && (
        <TestimonialForm user={user} onClose={() => setShowTestimonialForm(false)} />
      )}

      {/* Modern Professional Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative z-10">
          {/* Main Footer Content */}
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              
              {/* Company Info */}
              <div className="lg:col-span-4">
                <div className="flex items-center space-x-3 mb-6">
                  <img 
                    src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
                    alt="EXPO STAND Logo" 
                    className="w-12 h-10 filter brightness-0 invert" 
                  />
                  <div className="text-white">
                    <div className="text-2xl font-bold leading-tight">EXPO</div>
                    <div className="text-2xl font-bold leading-tight text-blue-400">STAND</div>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed text-lg">
                  Connecting global trade fair participants with premium contractors worldwide. 
                  Your trusted partner for exceptional exhibition experiences.
                </p>
                
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex items-center text-green-400">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-sm font-medium">500+ Active Projects</span>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3 text-gray-300">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                    </svg>
                    hello@expostand.com
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                    </svg>
                    Global Network - 50+ Countries
                  </div>
                </div>
              </div>

              {/* Platform Links */}
              <div className="lg:col-span-2">
                <h3 className="text-xl font-bold mb-6 text-white">Platform</h3>
                <ul className="space-y-3">
                  {[
                    'How It Works',
                    'Find Contractors', 
                    'Become Supplier',
                    'Premium Services',
                    'Success Stories',
                    'Help Center'
                  ].map((link, index) => (
                    <li key={index}>
                      <a href="#" className="text-gray-300 hover:text-blue-400 transition-colors duration-200 
                                          hover:translate-x-1 transform inline-block">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Exhibition Services */}
              <div className="lg:col-span-2">
                <h3 className="text-xl font-bold mb-6 text-white">Services</h3>
                <ul className="space-y-3">
                  {[
                    'Stand Design',
                    'Construction',
                    'Logistics Support',
                    'Installation',
                    'Event Management',
                    'Global Shipping'
                  ].map((service, index) => (
                    <li key={index}>
                      <a href="#" className="text-gray-300 hover:text-blue-400 transition-colors duration-200
                                          hover:translate-x-1 transform inline-block">
                        {service}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Newsletter & Social */}
              <div className="lg:col-span-4">
                <h3 className="text-xl font-bold mb-6 text-white">Stay Connected</h3>
                
                <p className="text-gray-300 mb-6">
                  Get the latest exhibition trends, contractor insights, and exclusive platform updates.
                </p>

                {/* Newsletter Signup */}
                <div className="relative mb-8">
                  <div className="flex">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-l-lg 
                               text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 
                               focus:bg-gray-700/50 transition-colors duration-200"
                    />
                    <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 
                                     text-white font-semibold rounded-r-lg hover:from-blue-700 
                                     hover:to-blue-800 transition-all duration-200 transform 
                                     hover:scale-105 focus:outline-none focus:ring-2 
                                     focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900">
                      Subscribe
                    </button>
                  </div>
                </div>

                {/* Social Media Icons */}
                <div className="mb-6">
                  <p className="text-sm text-gray-400 mb-4">Follow us on social media</p>
                  <div className="flex space-x-4">
                    {[
                      { name: 'LinkedIn', icon: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z' },
                      { name: 'Twitter', icon: 'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z' },
                      { name: 'Instagram', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
                      { name: 'Facebook', icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' }
                    ].map((social, index) => (
                      <a key={index} href="#" className="w-10 h-10 bg-gray-800/50 rounded-lg flex items-center 
                                                       justify-center hover:bg-blue-600 transition-all duration-300 
                                                       transform hover:scale-110 hover:rotate-6 group">
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors duration-200" 
                             fill="currentColor" viewBox="0 0 24 24">
                          <path d={social.icon} />
                        </svg>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    ISO Certified
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                    </svg>
                    SSL Secured
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
                <div className="flex flex-wrap items-center space-x-6 text-sm text-gray-400">
                  <a href="#" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
                  <a href="#" className="hover:text-white transition-colors duration-200">Terms of Service</a>
                  <a href="#" className="hover:text-white transition-colors duration-200">Cookie Settings</a>
                  <a href="#" className="hover:text-white transition-colors duration-200">GDPR Compliance</a>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span>© 2025 EXPO STAND. All rights reserved.</span>
                  <div className="flex items-center space-x-2">
                    <span>Made with</span>
                    <svg className="w-4 h-4 text-red-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                    </svg>
                    <span>for exhibitions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <DataModeProvider>
      <DropdownProvider>
      <NotificationProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<PrivatePage />} />
          <Route path="/1453" element={<HomePage />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/exhibitor/login" element={<ExhibitorLogin />} />
          <Route path="/contractor/login" element={<ContractorLogin />} />
          <Route path="/supplier/login" element={<SupplierLogin />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          
          {/* Signup Routes — with public navbar + footer */}
          <Route element={<SignupLayout />}>
            <Route path="/exhibitor/signup" element={<ExhibitorSignup />} />
            <Route path="/contractor/signup" element={<ContractorSignup />} />
            <Route path="/supplier/signup" element={<SupplierSignup />} />
            <Route path="/freelancer/signup" element={<FreelancerSignup />} />
            <Route path="/freelancer/login" element={<FreelancerLogin />} />
          </Route>

          {/* Freelancer Landing — has its own navbar + footer */}
          <Route path="/freelancer/landing" element={<FreelancerLanding />} />
          {/* Exhibitor Landing & Brief Routes */}
          <Route path="/exhibitor/landing" element={<ExhibitorLanding />} />
          <Route path="/exhibitor/brief-start" element={<ExhibitorLanding />} />
          <Route path="/exhibitor/brief-review" element={<ExhibitorLanding />} />
          <Route path="/exhibitor/brief-success" element={<ExhibitorBriefSuccess />} />
          
          {/* Contractor Landing Routes */}
          <Route path="/contractor/landing" element={<ContractorLanding />} />
          
          {/* Supplier Landing Routes */}
          <Route path="/supplier/landing" element={<SupplierLanding />} />
          
          {/* Get Free Quote Route */}
          <Route path="/get-quote" element={<GetFreeQuote />} />
          
          {/* Premium Quote Routes */}
          <Route path="/premium-quote" element={<PremiumQuoteHandler />} />
          
          {/* Premium Quote Test Pages - Keep for testing */}
          <Route path="/premium-quote-benefits" element={<PremiumQuoteBenefits />} />
          <Route path="/premium-quote-form" element={<PremiumQuoteForm />} />
          
          {/* City Detail Routes — self-contained layout (own header + footer) */}
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/cities" element={<LocationsPage />} />
          <Route path="/city/:citySlug" element={<CityDetail />} />
          <Route path="/en/city/:citySlug" element={<CityDetail />} />
          <Route path="/tr/city/:citySlug" element={<CityDetail />} />

          {/* Fair Detail — uses SignupLayout for navbar/footer */}
          <Route element={<SignupLayout />}>
            <Route path="/fair/:fairId" element={<FairDetail />} />
          </Route>
          <Route path="/dubai-exhibition-stand-builder" element={<Navigate to="/city/dubai" replace />} />
          <Route path="/istanbul-exhibition-stand-builder" element={<Navigate to="/city/istanbul" replace />} />
          <Route path="/berlin-exhibition-stand-builder" element={<Navigate to="/city/berlin" replace />} />
          <Route path="/milan-exhibition-stand-builder" element={<Navigate to="/city/milan" replace />} />
          <Route path="/frankfurt-exhibition-stand-builder" element={<Navigate to="/city/frankfurt" replace />} />
          <Route path="/paris-exhibition-stand-builder" element={<Navigate to="/city/paris" replace />} />
          <Route path="/amsterdam-exhibition-stand-builder" element={<Navigate to="/city/amsterdam" replace />} />
          <Route path="/barcelona-exhibition-stand-builder" element={<Navigate to="/city/barcelona" replace />} />
          <Route path="/hannover-exhibition-stand-builder" element={<Navigate to="/city/hannover" replace />} />
          <Route path="/hannover-messe-stand-builder" element={<Navigate to="/city/hannover" replace />} />
          {/* Category Landing Pages */}
          <Route path="/modular-stands" element={<CategoryLanding />} />
          <Route path="/custom-stands" element={<CategoryLanding />} />
          <Route path="/double-decker-stands" element={<CategoryLanding />} />
          {/* Multi-language alias routes */}
          <Route path="/en/:categorySlug" element={<CategoryLanding />} />
          <Route path="/tr/:categorySlug" element={<CategoryLanding />} />
          
          {/* Top 10 Contractors Route */}
          <Route path="/top-10-contractors" element={<Top10Contractors />} />

          {/* Public Brief Discovery - must be before :slug */}
          <Route path="/contractor/discover" element={<ContractorDiscover />} />

          {/* Public Search Pages */}
          <Route path="/find-contractors" element={<PublicFindContractors />} />
          <Route path="/find-contractors/:city" element={<PublicFindContractors />} />
          <Route path="/find-contractors/fair/:fairSlug" element={<PublicFindContractors />} />
          <Route path="/compare-contractors" element={<ContractorCompare />} />
          <Route path="/find-suppliers" element={<PublicFindSuppliers />} />
          <Route path="/find-suppliers/:city" element={<PublicFindSuppliers />} />
          <Route path="/find-suppliers/fair/:fairSlug" element={<PublicFindSuppliers />} />
          <Route path="/saved-searches" element={<SavedSearchesPage />} />


          {/* Team Member Profile Page - must be before /contractor/:slug */}
          <Route path="/contractor/:slug/team/:memberName" element={<TeamMemberPage />} />
          {/* Public Contractor Profile Page */}
          <Route path="/contractor/:slug" element={<ContractorPublicProfile />} />
          <Route path="/exhibitor/:slug" element={<ExhibitorPublicProfile />} />

          {/* Public Supplier Profile Page */}
          <Route path="/supplier/:slug" element={<SupplierPublicProfile />} />

          {/* Freelancer Public Routes */}
          <Route path="/find-freelancers" element={<PublicFindFreelancers />} />
          <Route path="/job-board" element={<JobBoardPage />} />
          <Route path="/freelancer/:slug" element={<FreelancerPublicProfile />} />
          
          {/* Onboarding Routes */}
          {/* Exhibitor Onboarding removed — signup auto-redirects to dashboard */}
          
          {/* Default app route - redirect to appropriate dashboard based on role */}
          <Route path="/app" element={<RequireAuth><AppRedirect /></RequireAuth>} />
          
          {/* Ultra Admin Routes with Authentication */}
          <Route path="/app/ultra" element={
            <RequireAuth>
              <RequireRole roles={['ULTRA_ADMIN']}>
                <UltraLayout />
              </RequireRole>
            </RequireAuth>
          }>
            <Route path="dashboard" element={<UltraDashboard />} />
            <Route path="orgs" element={<UltraOrgs />} />
            <Route path="users" element={<UltraUsers />} />
            <Route path="plans" element={<UltraPlans />} />
            <Route path="subscriptions" element={<UltraSubscriptions />} />
            <Route path="audit" element={<UltraAudit />} />
            <Route path="webhooks" element={<UltraWebhooks />} />
            <Route path="settings" element={<UltraSettings />} />
            <Route path="suppliers" element={<UltraSuppliers />} />
            <Route path="briefs" element={<UltraBriefs />} />
            <Route path="scheduler" element={<UltraScheduler />} />
            <Route path="cms" element={<UltraCMS />} />
            <Route path="dropdowns" element={<UltraDropdowns />} />
            <Route path="rules" element={<UltraRules />} />
            <Route path="testimonials" element={<UltraTestimonials />} />
            <Route path="inquiries" element={<UltraInquiries />} />
            <Route path="exhibitor-profiles" element={<UltraExhibitors />} />
            <Route path="fairs" element={<UltraFairs />} />
            <Route path="credits" element={<UltraCredits />} />
            <Route path="featured-listings" element={<UltraFeaturedListings />} />
            <Route path="organizers" element={<UltraOrganizers />} />
            <Route path="platform-features" element={<UltraPlatformFeatures />} />
            <Route path="email-management" element={<UltraEmailManagement />} />
            <Route path="freelancers" element={<UltraFreelancers />} />
            <Route path="job-posts" element={<UltraJobPosts />} />
            <Route index element={<UltraDashboard />} />
          </Route>

          {/* Exhibitor Brief Wizard — public, no auth required */}
          <Route path="/exhibitor/brief/new" element={<ExhibitorBriefWizard />} />

          {/* Exhibitor Onboarding (outside layout, full-page wizard) */}
          <Route path="/app/exhibitor/onboarding" element={
            <RequireAuth>
              <RequireRole roles={['EXHIBITOR_ADMIN', 'EXHIBITOR_MEMBER']}>
                <ExhibitorOnboarding />
              </RequireRole>
            </RequireAuth>
          } />

          {/* Exhibitor Panel Routes */}
          <Route path="/app/exhibitor" element={
            <RequireAuth>
              <RequireRole roles={['EXHIBITOR_ADMIN', 'EXHIBITOR_MEMBER']}>
                <ExhibitorLayout />
              </RequireRole>
            </RequireAuth>
          }>
            <Route path="dashboard" element={<ExhibitorDashboard />} />
            <Route path="briefs" element={<ExhibitorBriefs />} />
              <Route path="find-contractors" element={<FindContractors />} />
            <Route path="briefs/new" element={<ExhibitorBriefWizard portalMode />} />
            <Route path="proposals" element={<ExhibitorProposals />} />
            <Route path="profile" element={<ExhibitorCompanyProfile />} />
            <Route path="my-profile" element={<ExhibitorProfileEditor />} />
            <Route path="team" element={<ExhibitorTeam />} />
            <Route path="invoices" element={<ExhibitorInvoices />} />
            <Route path="billing" element={<BillingPage role="EXHIBITOR" />} />
            <Route path="messages" element={<MessagingPage role="EXHIBITOR" />} />
            <Route path="analytics" element={<ExhibitorAnalytics />} />
            <Route index element={<ExhibitorDashboard />} />
          </Route>

          {/* Contractor Panel Routes */}
          <Route path="/app/contractor" element={
            <RequireAuth>
              <RequireRole roles={['CONTRACTOR_ADMIN', 'CONTRACTOR_MEMBER']}>
                <ContractorLayout />
              </RequireRole>
            </RequireAuth>
          }>
            <Route path="dashboard" element={<ContractorDashboard />} />
            <Route path="projects" element={<ContractorProjects />} />
            <Route path="brief-board" element={<ContractorBriefBoard />} />
            <Route path="proposals" element={<ContractorMyProposals />} />
            <Route path="analytics" element={<ContractorAnalytics />} />
            <Route path="profile" element={<ContractorProfile />} />
            <Route path="team" element={<ContractorTeam />} />
            <Route path="invoices" element={<ContractorInvoices />} />
            <Route path="billing" element={<BillingPage role="CONTRACTOR" />} />
              <Route path="credits" element={<CreditsPage />} />
            <Route path="messages" element={<MessagingPage role="CONTRACTOR" />} />
            <Route path="inbox" element={<ContractorMessages />} />
            <Route index element={<ContractorDashboard />} />
          </Route>

          {/* Contractor Onboarding (outside layout, full-page wizard) */}
          <Route path="/app/contractor/onboarding" element={
            <RequireAuth>
              <RequireRole roles={['CONTRACTOR_ADMIN', 'CONTRACTOR_MEMBER']}>
                <ContractorOnboarding />
              </RequireRole>
            </RequireAuth>
          } />

          {/* Supplier Onboarding (outside layout, full-page wizard) */}
          <Route path="/app/supplier/onboarding" element={
            <RequireAuth>
              <RequireRole roles={['SUPPLIER_ADMIN', 'SUPPLIER_MEMBER']}>
                <SupplierOnboarding />
              </RequireRole>
            </RequireAuth>
          } />

          {/* Supplier Panel Routes */}
          <Route path="/app/supplier" element={
            <RequireAuth>
              <RequireRole roles={['SUPPLIER_ADMIN', 'SUPPLIER_MEMBER']}>
                <SupplierLayout />
              </RequireRole>
            </RequireAuth>
          }>
            <Route path="dashboard" element={<SupplierDashboard />} />
            <Route path="profile" element={<SupplierProfile />} />
            <Route path="catalogue" element={<SupplierCatalogue />} />
            <Route path="analytics" element={<SupplierAnalytics />} />
              <Route path="find-briefs" element={<FindBriefs />} />
            <Route path="messages" element={<MessagingPage role="SUPPLIER" />} />
            <Route path="team" element={<SupplierTeam />} />
            <Route path="invoices" element={<SupplierInvoices />} />
            <Route path="billing" element={<BillingPage role="SUPPLIER" />} />
            <Route index element={<SupplierDashboard />} />
          </Route>

          {/* Freelancer Panel Routes */}
          <Route path="/app/freelancer" element={
            <RequireAuth>
              <RequireRole roles={['FREELANCER_ADMIN', 'FREELANCER_MEMBER']}>
                <FreelancerLayout />
              </RequireRole>
            </RequireAuth>
          }>
            <Route path="dashboard" element={<FreelancerDashboard />} />
            <Route path="profile" element={<FreelancerProfilePage />} />
            <Route path="messages" element={<MessagingPage role="FREELANCER" />} />
            <Route path="invoices" element={<FreelancerInvoices />} />
            <Route index element={<FreelancerDashboard />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
      </NotificationProvider>
      </DropdownProvider>
    </DataModeProvider>
  );
}

export default App;